import { InjectionToken } from '@angular/core';
import {
  BasicCrud,
  Creator,
  Deleter,
  Editor,
  GetAll,
  GetById
} from '@/types/service.types';
import { EgiAppEntity } from '@/app/platform/platform-data/models/egi-app.entity';
import { NavigationItemEntity } from '@/app/platform/platform-data/models/navigation-item.entity';
import { UserGroupEntity } from '@/app/platform/platform-data/models/user-group.entity';
import { PageFeature } from '@ervo/page-framework';
import { FeatureAuthorizationEntity } from '@/app/platform/platform-data/models/feature-authorization.entity';
import { UserAccountEntity } from '@/app/platform/platform-data/models/user-account.entity';
import { AsyncResult } from '@common/result/asyncResult';
import { DeviceEntity } from '@/app/platform/platform-data/models/device.entity';
import { PresentUserData } from '@/app/platform/platform-data/models/present-user-data';
import { PlatformDataSourceQuery } from '@data/_generated-api/platform-api/models/platform-data-source-query';
import { Announcement } from '@/app/platform/platform-data/models/announcement';

export const EGI_APP_SERVICE = new InjectionToken<EgiAppService>('');

export interface EgiAppService
  extends GetAll<EgiAppEntity>,
    GetById<EgiAppEntity>,
    Deleter<EgiAppEntity>,
    Creator<EgiAppEntity>,
    Editor<EgiAppEntity> {
  getAll(): AsyncResult<EgiAppEntity[]>;
}

export const NAVIGATION_ITEM_SERVICE = new InjectionToken<NavigationItemService>('');

export interface NavigationItemService
  extends Deleter<NavigationItemEntity>,
    Creator<NavigationItemEntity> {
  move(navigationItem: NavigationItemEntity): AsyncResult<NavigationItemEntity>;

  reOrder(navigationItem: NavigationItemEntity): AsyncResult<NavigationItemEntity>;

  createMenuItem(navigationItem: NavigationItemEntity): AsyncResult<NavigationItemEntity>;

  changeName(navigationItem: NavigationItemEntity): AsyncResult<NavigationItemEntity>;

  changeConfiguration(navigationItem: NavigationItemEntity): AsyncResult<NavigationItemEntity>;
}

export const USER_GROUP_SERVICE = new InjectionToken<UserGroupService>('');

export interface UserGroupService
  extends GetAll<UserGroupEntity>,
    Creator<UserGroupEntity>,
    Deleter<UserGroupEntity>,
    Editor<UserGroupEntity>,
    GetById<UserGroupEntity> {
  getLinkedAppIds(userGroupId: string): AsyncResult<string[]>;

  linkAppToUserGroup(userGroup: UserGroupEntity, app: EgiAppEntity): AsyncResult<UserGroupEntity>;

  unlinkAppFromUserGroup(
    userGroup: UserGroupEntity,
    app: EgiAppEntity
  ): AsyncResult<UserGroupEntity>;

  authorizeNavigationItem(
    group: UserGroupEntity,
    navigationItem: NavigationItemEntity
  ): AsyncResult<UserGroupEntity>;

  authorizePageFeature(
    group: UserGroupEntity,
    navigationItemId: string,
    feature: PageFeature
  ): AsyncResult<UserGroupEntity>;

  unAuthorizeNavigationItem(
    group: UserGroupEntity,
    navigationItem: NavigationItemEntity
  ): AsyncResult<UserGroupEntity>;

  unAuthorizePageFeature(authorization: FeatureAuthorizationEntity): AsyncResult<UserGroupEntity>;

  addMember(group: UserGroupEntity, user: UserAccountEntity): AsyncResult<UserGroupEntity>;

  removeMember(group: UserGroupEntity, user: UserAccountEntity): AsyncResult<UserGroupEntity>;
}

export const USER_ACCOUNT_SERVICE = new InjectionToken<UserAccountService>('');

export interface UserAccountService extends GetAll<UserAccountEntity> {}

export const DEVICE_SERVICE = new InjectionToken<DeviceService>('');

export interface DeviceService
  extends GetAll<DeviceEntity>,
    Creator<DeviceEntity>,
    Editor<DeviceEntity>,
    Deleter<DeviceEntity> {
  getAllWithQuery(query: PlatformDataSourceQuery): AsyncResult<[DeviceEntity[], number]>;
}

export interface PresentDeviceUserFilters {
  userGroupIds?: string[];
}

export const PRESENT_DEVICE_USER_SERVICE = new InjectionToken<PresentDeviceUserService>('');

export interface PresentDeviceUserService {
  getAll(filter: PresentDeviceUserFilters): AsyncResult<PresentUserData[]>;

  forceStop(userId: string): AsyncResult<boolean>;

  forceStopAll(): AsyncResult<boolean>;

  forceInvalidate(userId: string): AsyncResult<boolean>;

  forceInvalidateAll(): AsyncResult<boolean>;
}

export const ANNOUNCEMENT_SERVICE = new InjectionToken<AnnouncementService>('');

export interface AnnouncementService extends BasicCrud<Announcement> {}
